import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { TID } from "../constants/testIds";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Lock, EnvelopeSimple, Buildings, Phone, User, ArrowLeft, ShieldCheck } from "@phosphor-icons/react";

const LOGIN_BG = "https://images.pexels.com/photos/16370914/pexels-photo-16370914.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { login, signupStart, signupVerify, signupResend, user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("signin");

  // sign in
  const [siEmail, setSiEmail] = useState("");
  const [siPw, setSiPw] = useState("");

  // create account — step 1 (info)
  const [rName, setRName] = useState("");
  const [rEmail, setREmail] = useState("");
  const [rPhone, setRPhone] = useState("");
  const [rPw, setRPw] = useState("");

  // create account — step 2 (code)
  const [step, setStep] = useState(1); // 1 = info, 2 = code
  const [code, setCode] = useState("");

  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (user && user !== false) nav("/", { replace: true });
  }, [user, nav]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setErr(""); setMsg(""); setBusy(true);
    const res = await login(siEmail.trim(), siPw);
    setBusy(false);
    if (!res.ok) setErr(res.error || "Sign-in failed");
    else nav("/", { replace: true });
  };

  const handleStartSignup = async (e) => {
    e.preventDefault();
    setErr(""); setMsg(""); setBusy(true);
    const res = await signupStart({
      email: rEmail.trim(),
      password: rPw,
      name: rName.trim(),
      phone: rPhone.trim(),
    });
    setBusy(false);
    if (!res.ok) { setErr(res.error); return; }
    setMsg(`We sent a 6-digit code to ${rEmail.trim()}. Enter it below to finish creating your account.`);
    setStep(2);
    setCode("");
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setErr(""); setMsg(""); setBusy(true);
    const res = await signupVerify(rEmail.trim(), code.trim());
    setBusy(false);
    if (!res.ok) { setErr(res.error); return; }
    nav("/", { replace: true });
  };

  const handleResend = async () => {
    setErr(""); setMsg(""); setBusy(true);
    const res = await signupResend(rEmail.trim());
    setBusy(false);
    if (!res.ok) setErr(res.error);
    else setMsg(`Sent a fresh code to ${rEmail.trim()}.`);
  };

  const goBackToInfo = () => {
    setStep(1);
    setCode("");
    setErr("");
    setMsg("");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:col-span-3 relative overflow-hidden bg-slate-900 text-white">
        <img src={LOGIN_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-slate-900/70 to-slate-900/95" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-white text-slate-900 grid place-items-center font-heading font-bold">C</div>
            <div className="font-heading text-xl font-bold tracking-tight">Choice Capital Solutions</div>
          </div>
          <div className="max-w-md">
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-300 mb-4">Lender Intelligence Platform</div>
            <h1 className="font-heading text-5xl font-bold tracking-tighter leading-[1.05]">
              Know exactly <span className="text-blue-400">which lender</span> funds the deal — before you submit it.
            </h1>
            <p className="mt-6 text-slate-300 text-base leading-relaxed">
              Searchable lender database, AI-powered bank statement pricing, and ranked recommendations built for ISOs and underwriters at Choice Capital.
            </p>
          </div>
          <div className="text-xs text-slate-400 font-mono">
            Restricted to @choicecapitalsolutions.com — Internal Use Only
          </div>
        </div>
      </div>

      {/* Right auth card */}
      <div className="lg:col-span-2 flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <div className="h-9 w-9 rounded-md bg-slate-900 text-white grid place-items-center font-heading font-bold">C</div>
            <div className="font-heading font-bold">Choice Capital</div>
          </div>

          <Tabs value={tab} onValueChange={(v) => { setTab(v); setErr(""); setMsg(""); setStep(1); }} className="w-full">
            <TabsList className="grid grid-cols-2 mb-8">
              <TabsTrigger value="signin">Sign in</TabsTrigger>
              <TabsTrigger value="register" data-testid={TID.loginToggleRegister}>Create account</TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <div className="mb-6">
                <h2 className="font-heading text-3xl font-bold tracking-tight">Sign in</h2>
                <p className="text-sm text-slate-500 mt-1">Use your @choicecapitalsolutions.com account.</p>
              </div>
              <form onSubmit={handleSignIn} className="space-y-4">
                <div>
                  <Label htmlFor="si-email">Email</Label>
                  <div className="relative mt-1">
                    <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input id="si-email" type="email" required autoFocus value={siEmail} onChange={(e) => setSiEmail(e.target.value)}
                      placeholder="you@choicecapitalsolutions.com"
                      className="pl-9" data-testid={TID.loginEmail} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="si-pw">Password</Label>
                  <div className="relative mt-1">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input id="si-pw" type="password" required value={siPw} onChange={(e) => setSiPw(e.target.value)}
                      className="pl-9" data-testid={TID.loginPassword} />
                  </div>
                </div>
                {err && <div data-testid={TID.authError} className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}
                {msg && <div data-testid={TID.authSuccess} className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-md">{msg}</div>}
                <Button type="submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid={TID.loginSubmit}>
                  {busy ? "Signing in…" : "Sign in"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              {step === 1 ? (
                <>
                  <div className="mb-6">
                    <h2 className="font-heading text-3xl font-bold tracking-tight">Create account</h2>
                    <p className="text-sm text-slate-500 mt-1">Only @choicecapitalsolutions.com emails are eligible. If you already have an account, this will reset your password. We&apos;ll email you a 6-digit code to confirm.</p>
                  </div>
                  <form onSubmit={handleStartSignup} className="space-y-4">
                    <div>
                      <Label htmlFor="r-name">Full name</Label>
                      <div className="relative mt-1">
                        <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input id="r-name" required value={rName} onChange={(e) => setRName(e.target.value)} className="pl-9" data-testid={TID.registerName} />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="r-email">Company email</Label>
                      <div className="relative mt-1">
                        <Buildings size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input id="r-email" type="email" required value={rEmail} onChange={(e) => setREmail(e.target.value)} placeholder="you@choicecapitalsolutions.com" className="pl-9" data-testid={TID.registerEmail} />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="r-phone">Mobile phone (used for future SMS verification)</Label>
                      <div className="relative mt-1">
                        <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input id="r-phone" required value={rPhone} onChange={(e) => setRPhone(e.target.value)} placeholder="(555) 555-0100" className="pl-9" data-testid={TID.registerPhone} />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="r-pw">Password (min 6 chars)</Label>
                      <div className="relative mt-1">
                        <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input id="r-pw" type="password" required minLength={6} value={rPw} onChange={(e) => setRPw(e.target.value)} className="pl-9" data-testid={TID.registerPassword} />
                      </div>
                    </div>
                    {err && <div data-testid={TID.authError} className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}
                    <Button type="submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid={TID.registerSubmit}>
                      {busy ? "Sending code…" : "Send verification code"}
                    </Button>
                  </form>
                </>
              ) : (
                <>
                  <button onClick={goBackToInfo} className="mb-4 inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900" data-testid="signup-back-to-info">
                    <ArrowLeft size={12} /> Edit info
                  </button>
                  <div className="mb-6">
                    <div className="h-10 w-10 rounded-md bg-emerald-50 text-emerald-700 grid place-items-center mb-3">
                      <ShieldCheck size={20} weight="duotone" />
                    </div>
                    <h2 className="font-heading text-3xl font-bold tracking-tight">Check your email</h2>
                    <p className="text-sm text-slate-500 mt-1">Enter the 6-digit code we sent to <span className="font-mono text-slate-700">{rEmail.trim()}</span>.</p>
                  </div>
                  <form onSubmit={handleVerify} className="space-y-4">
                    <div>
                      <Label htmlFor="r-code">Verification code</Label>
                      <Input
                        id="r-code"
                        required
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        pattern="[0-9]{6}"
                        maxLength={6}
                        autoFocus
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="123456"
                        className="text-center text-2xl tracking-[0.5em] font-mono"
                        data-testid="signup-code-input"
                      />
                    </div>
                    {err && <div data-testid={TID.authError} className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}
                    {msg && <div data-testid={TID.authSuccess} className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-md">{msg}</div>}
                    <Button type="submit" disabled={busy || code.length !== 6} className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="signup-verify-submit">
                      {busy ? "Verifying…" : "Verify & sign in"}
                    </Button>
                    <div className="text-center text-xs text-slate-500">
                      Didn&apos;t get it?{" "}
                      <button type="button" onClick={handleResend} disabled={busy} className="font-semibold text-slate-900 hover:underline" data-testid="signup-resend-code">
                        Resend code
                      </button>
                    </div>
                  </form>
                </>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
